import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { loginRequest, logoutRequest, refreshRequest, registerRequest } from './authApi';
import type { AccessTokenPayload, AuthUser, LoginInput, RegisterInput } from './authTypes';
import { storeAccessToken, tokenStorage } from './tokenStorage';

type AuthContextValue = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * The seawall access token's exact claim names aren't documented beyond
 * "user ID and permissions", so this pulls whatever looks like an id/email
 * out of the decoded payload rather than assuming a specific shape.
 */
function userFromPayload(payload: AccessTokenPayload): AuthUser {
  const rawId = payload.id ?? payload.ID ?? payload.sub ?? null;
  const rawEmail = typeof payload.email === 'string' ? payload.email : null;
  return {
    id: typeof rawId === 'string' || typeof rawId === 'number' ? rawId : null,
    email: rawEmail,
    claims: payload,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    setIsLoading(true);
    try {
      if (tokenStorage.isTokenActive()) {
        const payload = tokenStorage.getPayload();
        if (payload) {
          setUser(userFromPayload(payload));
          return;
        }
      }

      // RAM is empty (e.g. page was reloaded) — try to restore the session
      // silently using the HttpOnly `jwt` refresh cookie.
      const refreshed = await refreshRequest();
      if (!refreshed?.accessToken) {
        tokenStorage.clearToken();
        setUser(null);
        return;
      }

      const payload = storeAccessToken(refreshed.accessToken);
      setUser(userFromPayload(payload));
    } catch {
      tokenStorage.clearToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (input: LoginInput) => {
    const data = await loginRequest(input);
    const payload = storeAccessToken(data.accessToken);
    setUser(userFromPayload(payload));
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    // POST /register/ only creates the account — it doesn't return tokens —
    // so we log in immediately afterwards with the same credentials.
    await registerRequest(input);
    await login({ email: input.email, password: input.password });
  }, [login]);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      tokenStorage.clearToken();
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      register,
      logout,
    }),
    [user, isLoading, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
